/**
 * Módulo Bancos (Norma 43): lectura de extractos N43 clásicos y SEPA con
 * todos sus conceptos. Fase 1: carga, cuadre de saldos y revisión de movimientos.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Upload, CheckCircle2, AlertTriangle, Landmark, Filter } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { loadOrganizations, type Organization } from "@/lib/organizations";
import { formatNumber } from "@/lib/numberFormatting";
import { fullConcept, type N43Account } from "@/lib/norma43";
import { decodeBankStatement, parseBankStatement } from "@/lib/bankStatementImport";
import { Input } from "@/components/ui/input";
import AccountPicker from "@/components/bancos/AccountPicker";
import { kindForAccount, type PickableAccount } from "@/lib/unifiedAccounts";
import { suggestForMovements, type LoanInstallment, type MatchContext, type MatchSource } from "@/lib/bankMatcher";

const SOURCE_LABEL: Record<MatchSource, string> = {
  prestamo: "Préstamo", regla: "Regla", cliente: "Cliente", proveedor: "Proveedor", sin_asignar: "Sin asignar",
};
import { cn } from "@/lib/utils";

const money = (n: number) => `${formatNumber(n)} €`;
const fmtDate = (iso: string) => (iso ? iso.split("-").reverse().join("/") : "—");

type ColKey = "fecha" | "importe" | "contrapartida";

interface RowMeta {
  key: string;
  fecha: string;
  fechaRaw: string;
  importe: string;
  importeNum: number;
  contrapartida: string;
  concepto: string;
}

interface FilterOption {
  value: string;
  count: number;
}

const ColumnFilterMenu = ({
  label,
  options,
  selected,
  onToggle,
  onClear,
  onSelectNone,
  active,
}: {
  label: string;
  options: FilterOption[];
  selected?: Set<string>;
  onToggle: (v: string) => void;
  onClear: () => void;
  onSelectNone: () => void;
  active: boolean;
}) => {
  const [search, setSearch] = useState("");
  const shown = options.filter((o) => o.value.toLowerCase().includes(search.toLowerCase()));
  // Estilo Excel: sin filtro, todo está marcado.
  const isChecked = (v: string) => !selected || selected.has(v);
  const allChecked = options.every((o) => isChecked(o.value));
  return (
    <Popover onOpenChange={(o) => { if (o) setSearch(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Filtrar ${label}`}
          className={cn(
            "inline-flex items-center gap-1 rounded px-1 py-0.5 text-xs font-medium hover:bg-muted",
            active && "text-primary"
          )}
        >
          {label}
          <Filter className={cn("h-3 w-3", active ? "text-primary" : "text-muted-foreground")} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="bottom" avoidCollisions={false} className="w-64 p-0">
        <div className="border-b p-2">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar valor…" className="h-7 text-xs" />
        </div>
        <div className="h-56 overflow-y-auto p-1">
          {!search && options.length > 0 && (
            <label className="flex items-center gap-2 rounded px-2 py-1 text-xs font-medium hover:bg-muted cursor-pointer border-b mb-1">
              <Checkbox checked={allChecked} onCheckedChange={() => (allChecked ? onSelectNone() : onClear())} />
              <span className="flex-1">(Seleccionar todo)</span>
            </label>
          )}
          {shown.length === 0 && <p className="p-2 text-xs text-muted-foreground">Sin valores</p>}
          {shown.map((o) => (
            <label key={o.value} className="flex items-center gap-2 rounded px-2 py-1 text-xs hover:bg-muted cursor-pointer">
              <Checkbox
                checked={isChecked(o.value)}
                onCheckedChange={() => onToggle(o.value)}
              />
              <span className="flex-1 break-all">{o.value}</span>
              <span className="text-muted-foreground">{o.count}</span>
            </label>
          ))}
        </div>
        <div className="flex items-center justify-between border-t p-2 text-xs">
          <button type="button" className="underline text-muted-foreground" onClick={onClear}>
            Ver todos
          </button>
          <span>{selected ? `${selected.size} de ${options.length} visibles` : "Sin filtro"}</span>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const ConceptFilterMenu = ({
  value,
  onChange,
  active,
}: {
  value: string;
  onChange: (v: string) => void;
  active: boolean;
}) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        aria-label="Filtrar Concepto"
        className={cn(
          "inline-flex items-center gap-1 rounded px-1 py-0.5 text-xs font-medium hover:bg-muted",
          active && "text-primary"
        )}
      >
        Concepto completo
        <Filter className={cn("h-3 w-3", active ? "text-primary" : "text-muted-foreground")} />
      </button>
    </PopoverTrigger>
    <PopoverContent align="start" side="bottom" avoidCollisions={false} className="w-72 p-2 space-y-2">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="El concepto contiene…"
        className="h-7 text-xs"
        autoFocus
      />
      <div className="flex items-center justify-between text-xs">
        <button type="button" className="underline text-muted-foreground" onClick={() => onChange("")}>
          Limpiar
        </button>
        <span className="text-muted-foreground">Coincidencia en el texto completo</span>
      </div>
    </PopoverContent>
  </Popover>
);

const Bancos = () => {
  const { organizationId } = useAuth();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgId, setOrgId] = useState("");
  const [companies, setCompanies] = useState<{ id: string; name: string; organization_id: string }[]>([]);
  const [companyId, setCompanyId] = useState("");
  const [fileName, setFileName] = useState("");
  const [accounts, setAccounts] = useState<N43Account[]>([]);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [colFilters, setColFilters] = useState<Partial<Record<ColKey, Set<string>>>>({});
  const [conceptSearch, setConceptSearch] = useState("");

  useEffect(() => {
    supabase.from("company_masters").select("id, name, organization_id").order("name").then(({ data }) => setCompanies((data as any) ?? []));
    loadOrganizations().then(setOrgs);
  }, []);
  useEffect(() => { if (!orgId && organizationId) setOrgId(organizationId); }, [organizationId, orgId]);
  const orgCompanies = useMemo(() => companies.filter((c) => c.organization_id === orgId), [companies, orgId]);

  const [ctx, setCtx] = useState<MatchContext>({ loans: [], customers: [], suppliers: [] });
  const [catalog, setCatalog] = useState<PickableAccount[]>([]);
  useEffect(() => {
    setAccounts([]); setFileName(""); setExcluded(new Set()); setOverrides({});
    setColFilters({}); setConceptSearch("");
    setCatalog([]);
    if (!companyId) { setCtx({ loans: [], customers: [], suppliers: [] }); return; }
    (async () => {
      const fetchAll = async (table: "customer_master" | "supplier_master" | "company_account_catalog", cols: string) => {
        const out: any[] = [];
        for (let from = 0; ; from += 1000) {
          const { data } = await supabase.from(table).select(cols).eq("company_id", companyId).order("id").range(from, from + 999);
          out.push(...((data as any[]) ?? []));
          if (!data || data.length < 1000) break;
        }
        return { data: out };
      };
      const [loansRes, custRes, supRes, genRes] = await Promise.all([
        supabase.from("loan_contracts").select("id, contract_number, bank_name, loan_schedule(installment_number, due_date, total_amount, principal_amount, interest_amount, insurance)").eq("company_id", companyId),
        fetchAll("customer_master", "id, account_code, description, tax_id"),
        fetchAll("supplier_master", "id, account_code, description, tax_id"),
        fetchAll("company_account_catalog", "id, account, description"),
      ]);
      const loans: LoanInstallment[] = ((loansRes.data as any[]) ?? []).flatMap((c) =>
        (c.loan_schedule ?? []).map((s: any) => ({
          contractNumber: c.contract_number, bankName: c.bank_name ?? "", installmentNumber: s.installment_number,
          dueDate: s.due_date, total: Number(s.total_amount), principal: Number(s.principal_amount),
          interest: Number(s.interest_amount), insurance: Number(s.insurance ?? 0),
        })));
      const map = (rows: any[] | null) => (rows ?? []).map((r) => ({ account: r.account_code, name: r.description, taxId: r.tax_id }));
      const customers = map(custRes.data), suppliers = map(supRes.data);
      setCtx({ loans, customers, suppliers });
      const seen = new Set<string>();
      const all: PickableAccount[] = [];
      const push = (a: PickableAccount) => { if (a.account && !seen.has(a.account)) { seen.add(a.account); all.push(a); } };
      suppliers.forEach((e) => push({ account: e.account, name: e.name ?? "", taxId: e.taxId, kind: "proveedor" }));
      customers.forEach((e) => push({ account: e.account, name: e.name ?? "", taxId: e.taxId, kind: "cliente" }));
      (genRes.data ?? []).forEach((r: any) => push({ account: r.account, name: r.description ?? "", kind: kindForAccount(r.account ?? "") }));
      all.sort((a, b) => a.account.localeCompare(b.account));
      setCatalog(all);
    })();
  }, [companyId]);

  const suggestions = useMemo(() => accounts.map((a) => suggestForMovements(a.movements, ctx)), [accounts, ctx]);

  const rowMeta = useMemo<RowMeta[]>(() => accounts.flatMap((acc, ai) => {
    const sugg = suggestions[ai] ?? [];
    return acc.movements.map((m, mi) => {
      const key = `${ai}-${m.index}`;
      const manual = overrides[key];
      const s = sugg[mi];
      const counterpart = manual !== undefined ? manual : (s && s.source !== "sin_asignar" ? s.account : "");
      return {
        key,
        fecha: fmtDate(m.operationDate),
        fechaRaw: m.operationDate,
        importe: money(m.amount),
        importeNum: m.amount,
        contrapartida: counterpart,
        concepto: fullConcept(m).toLowerCase(),
      };
    });
  }), [accounts, suggestions, overrides]);

  const metaByKey = useMemo(() => new Map(rowMeta.map((r) => [r.key, r])), [rowMeta]);

  const buildOptions = useCallback((get: (r: RowMeta) => string, sort: (a: RowMeta, b: RowMeta) => number): FilterOption[] => {
    const counts = new Map<string, number>();
    rowMeta.forEach((r) => {
      const v = get(r);
      counts.set(v, (counts.get(v) ?? 0) + 1);
    });
    const sorted = [...rowMeta].sort(sort);
    const seen = new Set<string>();
    const out: FilterOption[] = [];
    sorted.forEach((r) => {
      const v = get(r);
      if (seen.has(v)) return;
      seen.add(v);
      out.push({ value: v, count: counts.get(v) ?? 0 });
    });
    return out;
  }, [rowMeta]);

  const fechaOptions = useMemo(() => buildOptions((r) => r.fecha, (a, b) => a.fechaRaw.localeCompare(b.fechaRaw)), [buildOptions]);
  const importeOptions = useMemo(() => buildOptions((r) => r.importe, (a, b) => a.importeNum - b.importeNum), [buildOptions]);
  const contrapartidaOptions = useMemo(
    () => buildOptions((r) => r.contrapartida || "(sin asignar)", (a, b) => {
      const av = a.contrapartida ? a.contrapartida : "￡";
      const bv = b.contrapartida ? b.contrapartida : "￡";
      return av.localeCompare(bv);
    }),
    [buildOptions]
  );

  const optionsByCol = useMemo(() => ({ fecha: fechaOptions, importe: importeOptions, contrapartida: contrapartidaOptions }), [fechaOptions, importeOptions, contrapartidaOptions]);
  const toggleFilterValue = useCallback((col: ColKey, v: string) => setColFilters((p) => {
    const all = optionsByCol[col].map((o) => o.value);
    const cur = p[col];
    // Estilo Excel: cada casilla marcada se ve; pinchar invierte solo ese valor.
    const n = cur ? new Set(cur) : new Set(all);
    if (n.has(v)) n.delete(v); else n.add(v);
    const next = { ...p };
    if (all.every((x) => n.has(x))) delete next[col]; else next[col] = n;
    return next;
  }), [optionsByCol]);
  const clearFilter = useCallback((col: ColKey) => setColFilters((p) => {
    const n = { ...p }; delete n[col]; return n;
  }), []);
  const clearAllFilters = useCallback(() => { setColFilters({}); setConceptSearch(""); }, []);

  const matchesFilters = useCallback((meta: RowMeta | undefined) => {
    if (!meta) return false;
    const f = colFilters;
    if (f.fecha && !f.fecha.has(meta.fecha)) return false;
    if (f.importe && !f.importe.has(meta.importe)) return false;
    if (f.contrapartida && !f.contrapartida.has(meta.contrapartida || "(sin asignar)")) return false;
    if (conceptSearch && !meta.concepto.includes(conceptSearch.toLowerCase())) return false;
    return true;
  }, [colFilters, conceptSearch]);

  const hasActiveFilters =
    conceptSearch.trim().length > 0 ||
    (["fecha", "importe", "contrapartida"] as ColKey[]).some((c) => colFilters[c] !== undefined);

  const handleFile = async (file?: File) => {
    if (!file) return;
    if (!companyId) {
      toast({ title: "Elige primero la empresa", description: "Selecciona asesoría y empresa antes de subir el extracto.", variant: "destructive" });
      return;
    }
    let parsed: N43Account[];
    try {
      const buf = await file.arrayBuffer();
      parsed = parseBankStatement(decodeBankStatement(buf));
    } catch (error) {
      toast({ title: "No se ha podido cargar el extracto", description: error instanceof Error ? error.message : "No se puede leer el fichero.", variant: "destructive" });
      return;
    }
    if (parsed.length === 0) {
      toast({ title: "Formato de extracto no reconocido", description: "Selecciona un extracto Norma 43 o el TXT de consulta de movimientos de Santander.", variant: "destructive" });
      return;
    }
    setFileName(file.name);
    setAccounts(parsed);
    setExcluded(new Set()); setOverrides({});
    clearAllFilters();
  };

  const toggle = (key: string) => setExcluded((prev) => {
    const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n;
  });

  return (
    <div className="min-h-screen bg-background">
      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center gap-3 flex-wrap">
          <BackButton />
          <Landmark className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-serif">Bancos</h1>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
          <Select value={orgId} onValueChange={(v) => { setOrgId(v); setCompanyId(""); }}>
            <SelectTrigger><SelectValue placeholder="Asesoría" /></SelectTrigger>
            <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger><SelectValue placeholder="Empresa" /></SelectTrigger>
            <SelectContent>{orgCompanies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div
          aria-disabled={!companyId}
          onDragOver={(e) => { e.preventDefault(); if (!companyId) e.dataTransfer.dropEffect = "none"; }}
          onDrop={(e) => { e.preventDefault(); if (!companyId) return; handleFile(e.dataTransfer.files?.[0]); }}
          className={cn("rounded-lg border-2 border-dashed p-8 text-center space-y-3", !companyId && "opacity-50 cursor-not-allowed")}
        >
          <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {companyId ? "Arrastra aquí el extracto Norma 43 o el TXT de movimientos de Santander" : "Selecciona asesoría y empresa para poder subir el extracto"}
          </p>
          <Button onClick={() => companyId && inputRef.current?.click()} disabled={!companyId}>
            {companyId ? "Elegir fichero" : "Elige primero la empresa"}
          </Button>
          <input ref={inputRef} type="file" accept=".n43,.csb,.txt,.aeb,.dat" className="hidden" disabled={!companyId}
            onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }} />
          {fileName && <p className="text-xs text-muted-foreground">{fileName}</p>}
          {companyId && (
            <p className="text-xs text-muted-foreground">
              Para proponer cuentas: {ctx.loans.length} cuotas de préstamos · {ctx.customers.length} clientes · {ctx.suppliers.length} proveedores
            </p>
          )}
        </div>

        {accounts.length > 0 && (
          <div className={cn("flex items-center gap-3 text-sm", !hasActiveFilters && "invisible")}>
            <span className="text-muted-foreground">Filtros activos</span>
            <Button variant="outline" size="sm" onClick={clearAllFilters} disabled={!hasActiveFilters}>Quitar todos los filtros</Button>
          </div>
        )}

        {accounts.map((acc, ai) => {
          const sugg = suggestions[ai] ?? [];
          const assigned = acc.movements.filter((m, mi) => {
            const o = overrides[`${ai}-${m.index}`];
            return o !== undefined ? o.length > 0 : sugg[mi]?.source !== "sin_asignar";
          }).length;
          const visible = acc.movements.filter((m, mi) => matchesFilters(metaByKey.get(`${ai}-${m.index}`))).length;
          return (
          <section key={ai} className="space-y-3">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border p-4 text-sm">
              <span className="font-medium">{acc.holder || "Cuenta"} · {acc.bank} {acc.office} {acc.account}</span>
              {acc.sourceFormat === "santander-txt" && <Badge variant="outline">Santander TXT</Badge>}
              <span>{fmtDate(acc.startDate)} – {fmtDate(acc.endDate)}</span>
              <span>Saldo inicial: <b>{money(acc.initialBalance)}</b></span>
              <span>Cargos ({acc.computed.debitCount}): <b>{money(acc.computed.debitTotal)}</b></span>
              <span>Abonos ({acc.computed.creditCount}): <b>{money(acc.computed.creditTotal)}</b></span>
              <span>Saldo final: <b>{money(acc.computed.finalBalance)}</b></span>
              {acc.balanced ? (
                <span className="flex items-center gap-1 text-primary"><CheckCircle2 className="h-4 w-4" /> Cuadra con el banco</span>
              ) : (
                <span className="flex items-center gap-1 text-destructive"><AlertTriangle className="h-4 w-4" /> {acc.sourceFormat === "santander-txt" ? "No cuadra con los saldos de los movimientos" : "No cuadra con los totales del fichero"}</span>
              )}
              <span>Cuenta propuesta: <b>{assigned} de {acc.movements.length}</b> ({acc.movements.length ? Math.round((assigned / acc.movements.length) * 100) : 0} %)</span>
              <span>Mostrando <b>{visible}</b> de {acc.movements.length} movimientos</span>
            </div>

            <div className="rounded-lg border overflow-x-auto">
              <table className="w-full text-sm table-fixed min-w-[900px]">
                <colgroup><col className="w-20" /><col className="w-28" /><col /><col className="w-36" /><col className="w-72" /></colgroup>
                <thead className="bg-muted/50 text-left text-xs">
                  <tr>
                    <th className="px-3 py-2 w-10">Incluir</th>
                    <th className="px-3 py-2">
                      <ColumnFilterMenu
                        label="Fecha"
                        options={fechaOptions}
                        selected={colFilters.fecha}
                        onToggle={(v) => toggleFilterValue("fecha", v)}
                        onClear={() => clearFilter("fecha")}
                        onSelectNone={() => setColFilters((p) => ({ ...p, fecha: new Set<string>() }))}
                        active={colFilters.fecha !== undefined}
                      />
                    </th>
                    <th className="px-3 py-2 w-[40%]">
                      <ConceptFilterMenu
                        value={conceptSearch}
                        onChange={setConceptSearch}
                        active={conceptSearch.trim().length > 0}
                      />
                    </th>
                    <th className="px-3 py-2 text-right">
                      <ColumnFilterMenu
                        label="Importe"
                        options={importeOptions}
                        selected={colFilters.importe}
                        onToggle={(v) => toggleFilterValue("importe", v)}
                        onClear={() => clearFilter("importe")}
                        onSelectNone={() => setColFilters((p) => ({ ...p, importe: new Set<string>() }))}
                        active={colFilters.importe !== undefined}
                      />
                    </th>
                    <th className="px-3 py-2">
                      <ColumnFilterMenu
                        label="Contrapartida"
                        options={contrapartidaOptions}
                        selected={colFilters.contrapartida}
                        onToggle={(v) => toggleFilterValue("contrapartida", v)}
                        onClear={() => clearFilter("contrapartida")}
                        onSelectNone={() => setColFilters((p) => ({ ...p, contrapartida: new Set<string>() }))}
                        active={colFilters.contrapartida !== undefined}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {acc.movements.map((m, mi) => {
                    const key = `${ai}-${m.index}`;
                    const meta = metaByKey.get(key);
                    if (hasActiveFilters && !matchesFilters(meta)) return null;
                    const off = excluded.has(key);
                    const s = sugg[mi];
                    const manual = overrides[key];
                    const text = fullConcept(m);
                    return (
                      <tr key={key} className={cn("border-t align-top", off && "opacity-40")}>
                        <td className="px-3 py-2"><Checkbox checked={!off} onCheckedChange={() => toggle(key)} aria-label="Incluir movimiento" /></td>
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(m.operationDate)}</td>
                        <td className="px-3 py-2 break-words">
                          {m.concepts.length === 0 && !text ? <span className="text-muted-foreground">—</span> : (
                            <div className="space-y-0.5">
                              {(m.concepts.length ? m.concepts : [text]).map((c, ci) => (
                                <div key={ci} className="flex gap-2">
                                  <span className="font-mono text-[10px] text-muted-foreground pt-0.5 shrink-0">L{ci + 1}</span>
                                  <span className="whitespace-pre-wrap">{c}</span>
                                </div>
                              ))}
                              {(() => {
                                const refs = [["Doc", m.documentNumber], ["Ref. 1", m.reference1], ["Ref. 2", m.reference2]]
                                  .filter(([, v]) => v && !/^0+$/.test(v));
                                return refs.length ? (
                                  <div className="pt-1 text-xs text-muted-foreground font-mono">
                                    {refs.map(([k, v]) => `${k}: ${v}`).join(" · ")}
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          )}
                        </td>
                        <td className={cn("px-3 py-2 text-right whitespace-nowrap font-medium", m.amount < 0 ? "text-destructive" : "text-primary")}>{money(m.amount)}</td>
                        <td className="px-3 py-2 text-xs min-w-56 space-y-1">
                          <AccountPicker
                            value={manual ?? (s && s.source !== "sin_asignar" ? s.account : "")}
                            accounts={catalog}
                            onChange={(acc) => setOverrides((p) => ({ ...p, [key]: acc }))}
                          />
                          {manual ? (() => { const c = catalog.find((x) => x.account === manual); return c ? <div className="text-muted-foreground truncate">{c.name}</div> : null; })() : null}
                          {manual !== undefined ? (
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">Manual</Badge>
                              <button type="button" className="underline text-muted-foreground" onClick={() => setOverrides((p) => { const n = { ...p }; delete n[key]; return n; })}>
                                Volver a la propuesta
                              </button>
                            </div>
                          ) : !s || s.source === "sin_asignar" ? (
                            <Badge variant="outline" className="text-muted-foreground">Sin asignar</Badge>
                          ) : (
                            <div className="space-y-1">
                              <Badge variant="secondary">{SOURCE_LABEL[s.source]}</Badge>
                              <div>{s.label}</div>
                              {s.split?.map((x) => (
                                <div key={x.account} className="flex justify-between gap-3 text-muted-foreground">
                                  <span><span className="font-mono">{x.account}</span> {x.label}</span>
                                  <span>{money(x.amount)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
          );
        })}
      </main>
    </div>
  );
};

export default Bancos;
